import React, { useState, useEffect, useCallback } from 'react';
import {
  Building2, CreditCard, Shield, TrendingUp, TrendingDown,
  AlertCircle, CheckCircle2, Clock, DollarSign, BarChart3,
  Plus, Edit2, RefreshCw, ChevronDown, ChevronUp, X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { accountingAPI, bankAccountsAPI, bankTransactionsAPI } from '../services/api';
import Modal from '../components/Modal';
import { useAuth } from '../context/AuthContext';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TX_CATEGORIES = [
  'Income', 'Rent', 'Salaries & Wages', 'Utilities', 'Insurance',
  'IT & Software', 'Office Supplies', 'Professional Fees', 'Bank Charges',
  'Filing Fees', 'Travel', 'Advertising', 'Tax Payment', 'Trust Transfer', 'Other',
];

const EMPTY_ACCOUNT = { account_name: '', account_type: 'Operating', bank_name: '', account_number: '', current_balance: '' };
const EMPTY_TX = { bank_account: '', date: new Date().toISOString().slice(0, 10), description: '', amount: '', transaction_type: 'Debit', category: '', reference: '', notes: '' };

// ── Sub-components defined OUTSIDE parent to avoid re-mount on each render ───

function KpiCard({ icon: Icon, label, value, sub, color = 'blue', trend }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    amber: 'bg-amber-50 text-amber-700',
    red: 'bg-red-50 text-red-700',
    indigo: 'bg-indigo-50 text-indigo-700',
  };
  return (
    <div className="card p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors[color]}`}>
          <Icon size={20} />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`mt-3 flex items-center gap-1 text-xs font-medium ${trend >= 0 ? 'text-red-600' : 'text-green-600'}`}>
          {trend >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
          {trend >= 0 ? '+' : ''}{trend}% vs last month
        </div>
      )}
    </div>
  );
}

function BankAccountCard({ acct, onEdit }) {
  const typeIcon = acct.account_type === 'Credit Card' ? CreditCard : Building2;
  const Icon = typeIcon;
  const bal = parseFloat(acct.current_balance || 0);
  return (
    <div className="card p-5 border-l-4 border-blue-600">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <Icon size={16} className="text-blue-700" />
          </div>
          <div>
            <p className="font-semibold text-gray-800 text-sm">{acct.account_name}</p>
            <p className="text-xs text-gray-500">{acct.bank_name}{acct.account_number ? ` · ${acct.account_number}` : ''}</p>
          </div>
        </div>
        <button onClick={() => onEdit(acct)} className="p-1 text-gray-400 hover:text-blue-600">
          <Edit2 size={13} />
        </button>
      </div>
      <p className={`text-xl font-bold ${bal < 0 ? 'text-red-600' : 'text-gray-900'}`}>
        US${fmt(bal)}
      </p>
      <p className="text-xs text-gray-500 mt-1">Running balance</p>
      {acct.unreviewed_count > 0 && (
        <div className="mt-3 flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 px-2 py-1.5 rounded-lg">
          <AlertCircle size={12} />
          {acct.unreviewed_count} transaction{acct.unreviewed_count !== 1 ? 's' : ''} need review
        </div>
      )}
    </div>
  );
}

function TrustSummaryCard({ summary }) {
  return (
    <div className="card p-5 border-l-4 border-indigo-600">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
            <Shield size={16} className="text-indigo-700" />
          </div>
          <div>
            <p className="font-semibold text-gray-800 text-sm">Trust Accounts</p>
            <p className="text-xs text-gray-500">{summary.account_count} client account{summary.account_count !== 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>
      <p className="text-xl font-bold text-gray-900">US${fmt(summary.total_balance)}</p>
      <p className="text-xs text-gray-500 mt-1">Total client funds held</p>
      <div className="mt-3 flex items-center gap-1.5 text-xs text-indigo-700 bg-indigo-50 px-2 py-1.5 rounded-lg">
        <CheckCircle2 size={12} />
        Separate from firm funds (fiduciary)
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    Unreviewed: 'bg-red-100 text-red-700',
    Categorized: 'bg-yellow-100 text-yellow-700',
    Matched: 'bg-blue-100 text-blue-700',
    Reconciled: 'bg-green-100 text-green-700',
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${map[status] || 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Accounting() {
  const { user } = useAuth();
  const [dashData, setDashData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(false);
  const [plExpanded, setPlExpanded] = useState(true);
  const [txExpanded, setTxExpanded] = useState(true);
  const [txFilter, setTxFilter] = useState('Unreviewed');

  // Modals
  const [accountModal, setAccountModal] = useState(false);
  const [txModal, setTxModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [accountForm, setAccountForm] = useState(EMPTY_ACCOUNT);
  const [txForm, setTxForm] = useState(EMPTY_TX);
  const [saving, setSaving] = useState(false);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await accountingAPI.dashboard();
      setDashData(data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Access denied — Accounting is restricted to Admin and Accountant roles.');
      } else {
        toast.error('Failed to load accounting data.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTransactions = useCallback(async () => {
    setTxLoading(true);
    try {
      const params = {};
      if (txFilter && txFilter !== 'All') params.status = txFilter;
      const { data } = await bankTransactionsAPI.list(params);
      setTransactions(data.results || data);
    } finally {
      setTxLoading(false);
    }
  }, [txFilter]);

  const loadBankAccounts = useCallback(async () => {
    const { data } = await bankAccountsAPI.list();
    setBankAccounts(data.results || data);
  }, []);

  useEffect(() => {
    loadDashboard();
    loadBankAccounts();
  }, [loadDashboard, loadBankAccounts]);

  useEffect(() => { loadTransactions(); }, [loadTransactions]);

  const handleSaveAccount = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingAccount) {
        await bankAccountsAPI.update(editingAccount.id, accountForm);
        toast.success('Account updated');
      } else {
        await bankAccountsAPI.create(accountForm);
        toast.success('Bank account added');
      }
      setAccountModal(false);
      loadDashboard();
      loadBankAccounts();
    } catch {
      toast.error('Failed to save account');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTx = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await bankTransactionsAPI.create({ ...txForm, amount: parseFloat(txForm.amount) });
      toast.success('Transaction added');
      setTxModal(false);
      setTxForm(EMPTY_TX);
      loadDashboard();
      loadTransactions();
    } catch {
      toast.error('Failed to save transaction');
    } finally {
      setSaving(false);
    }
  };

  const updateTxStatus = async (txId, status, category) => {
    try {
      const payload = { status };
      if (category !== undefined) payload.category = category;
      await bankTransactionsAPI.update(txId, payload);
      loadTransactions();
      loadDashboard();
    } catch {
      toast.error('Failed to update transaction');
    }
  };

  const openAddAccount = () => {
    setEditingAccount(null);
    setAccountForm(EMPTY_ACCOUNT);
    setAccountModal(true);
  };

  const openEditAccount = (acct) => {
    setEditingAccount(acct);
    setAccountForm({
      account_name: acct.account_name,
      account_type: acct.account_type,
      bank_name: acct.bank_name,
      account_number: acct.account_number || '',
      current_balance: acct.current_balance,
    });
    setAccountModal(true);
  };

  const openAddTx = () => {
    setTxForm({ ...EMPTY_TX, bank_account: bankAccounts[0]?.id || '' });
    setTxModal(true);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-96">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!dashData) {
    return (
      <div className="p-8 text-center text-gray-500">
        <AlertCircle size={40} className="mx-auto mb-3 text-gray-300" />
        <p>Accounting data unavailable. Your role may not have access.</p>
      </div>
    );
  }

  const { bank_accounts, trust_summary, accounts_receivable, operating_expenses, profit_and_loss, pl_totals } = dashData;
  const ar = accounts_receivable;
  const opExp = operating_expenses;

  return (
    <div className="p-6 space-y-8 max-w-7xl mx-auto">

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Accounting</h1>
          <p className="text-sm text-gray-500 mt-0.5">Mutatu and Partners — Financial Overview</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={openAddTx}
            className="flex items-center gap-1.5 bg-white border border-gray-200 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg hover:bg-gray-50"
          >
            <Plus size={15} /> Add Transaction
          </button>
          <button
            onClick={openAddAccount}
            className="flex items-center gap-1.5 bg-blue-700 text-white text-sm font-semibold px-3 py-2 rounded-lg hover:bg-blue-800"
          >
            <Plus size={15} /> Add Bank Account
          </button>
        </div>
      </div>

      {/* ── Section 1: Firm Accounts ── */}
      <section>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Firm Accounts</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {bank_accounts.length === 0 && (
            <div className="col-span-full card p-6 text-center text-gray-400 text-sm">
              No bank accounts configured.{' '}
              <button onClick={openAddAccount} className="text-blue-600 hover:underline">Add one now</button>
            </div>
          )}
          {bank_accounts.map(acct => (
            <BankAccountCard key={acct.id} acct={acct} onEdit={openEditAccount} />
          ))}
          <TrustSummaryCard summary={trust_summary} />
        </div>
      </section>

      {/* ── Section 2: AR + Operating Expenses ── */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Accounts Receivable */}
        <div className="card p-5">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Accounts Receivable</h2>
          <div className="flex items-end gap-2 mb-1">
            <span className="text-3xl font-bold text-gray-900">US${fmt(ar.total_outstanding)}</span>
          </div>
          <p className="text-xs text-gray-500 mb-5">Total outstanding client invoices</p>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-green-50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-green-700">{ar.paid_this_month}</p>
              <p className="text-xs text-green-600 mt-0.5">Invoices paid this month</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-amber-700">{ar.total_invoices_owing}</p>
              <p className="text-xs text-amber-600 mt-0.5">Invoices with balances owing</p>
            </div>
          </div>
        </div>

        {/* Operating Expenses */}
        <div className="card p-5">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Operating Expenses</h2>

          <div className="grid grid-cols-2 gap-4 mb-5">
            <div>
              <p className="text-xs text-gray-500">This Month</p>
              <p className="text-2xl font-bold text-gray-900 mt-0.5">US${fmt(opExp.this_month)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Last Month</p>
              <p className="text-xl font-semibold text-gray-600 mt-0.5">US${fmt(opExp.last_month)}</p>
            </div>
          </div>

          {opExp.pct_change !== null && (
            <div className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full mb-5 ${
              opExp.pct_change > 0 ? 'bg-red-50 text-red-700' : opExp.pct_change < 0 ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'
            }`}>
              {opExp.pct_change > 0 ? <TrendingUp size={14} /> : opExp.pct_change < 0 ? <TrendingDown size={14} /> : null}
              {opExp.pct_change > 0 ? '+' : ''}{opExp.pct_change}% vs last month
            </div>
          )}

          <div className="grid grid-cols-3 gap-2">
            {[
              { label: 'Entered', val: opExp.created_count, color: 'text-gray-700 bg-gray-50' },
              { label: 'Matched', val: opExp.matched_count, color: 'text-blue-700 bg-blue-50' },
              { label: 'Reconciled', val: opExp.reconciled_count, color: 'text-green-700 bg-green-50' },
            ].map(({ label, val, color }) => (
              <div key={label} className={`rounded-xl p-3 text-center ${color}`}>
                <p className="text-xl font-bold">{val}</p>
                <p className="text-xs mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Section 3: Profit & Loss ── */}
      <section className="card p-0 overflow-hidden">
        <button
          className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 border-b hover:bg-gray-100 transition-colors"
          onClick={() => setPlExpanded(v => !v)}
        >
          <div className="flex items-center gap-2">
            <BarChart3 size={16} className="text-blue-700" />
            <span className="text-sm font-semibold text-gray-800">Profit &amp; Loss — Last 12 Months</span>
          </div>
          {plExpanded ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
        </button>

        {plExpanded && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Month</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-green-700 uppercase">Income</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-red-700 uppercase">Expenses</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-700 uppercase">Net Income</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {profit_and_loss.map((row) => (
                  <tr key={row.month_key} className="hover:bg-gray-50">
                    <td className="px-5 py-3 text-gray-700 font-medium">{row.month}</td>
                    <td className="px-5 py-3 text-right text-green-700">US${fmt(row.income)}</td>
                    <td className="px-5 py-3 text-right text-red-600">US${fmt(row.expenses)}</td>
                    <td className={`px-5 py-3 text-right font-semibold ${row.net >= 0 ? 'text-gray-900' : 'text-red-700'}`}>
                      {row.net < 0 ? '(' : ''}US${fmt(Math.abs(row.net))}{row.net < 0 ? ')' : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                <tr>
                  <td className="px-5 py-3 font-bold text-gray-800 text-sm">TOTAL (12 months)</td>
                  <td className="px-5 py-3 text-right font-bold text-green-700">US${fmt(pl_totals.income)}</td>
                  <td className="px-5 py-3 text-right font-bold text-red-600">US${fmt(pl_totals.expenses)}</td>
                  <td className={`px-5 py-3 text-right font-bold text-base ${pl_totals.net >= 0 ? 'text-blue-800' : 'text-red-700'}`}>
                    {pl_totals.net < 0 ? '(' : ''}US${fmt(Math.abs(pl_totals.net))}{pl_totals.net < 0 ? ')' : ''}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>

      {/* ── Section 4: Bank Transactions ── */}
      <section className="card p-0 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 bg-gray-50 border-b">
          <div className="flex items-center gap-2">
            <RefreshCw size={16} className="text-blue-700" />
            <span className="text-sm font-semibold text-gray-800">Bank Transactions</span>
          </div>
          <div className="flex items-center gap-2">
            <select
              className="input text-xs py-1 px-2 h-8"
              value={txFilter}
              onChange={e => setTxFilter(e.target.value)}
            >
              {['All', 'Unreviewed', 'Categorized', 'Matched', 'Reconciled'].map(s => (
                <option key={s}>{s}</option>
              ))}
            </select>
            <button onClick={() => setTxExpanded(v => !v)} className="p-1 text-gray-400 hover:text-gray-700">
              {txExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>
        </div>

        {txExpanded && (
          txLoading ? (
            <div className="py-10 text-center text-gray-400 text-sm">Loading transactions…</div>
          ) : transactions.length === 0 ? (
            <div className="py-10 text-center text-gray-400 text-sm">
              No transactions found.{' '}
              <button onClick={openAddTx} className="text-blue-600 hover:underline">Add one manually</button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    {['Date', 'Account', 'Description', 'Type', 'Amount', 'Category', 'Status', 'Actions'].map(h => (
                      <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {transactions.map(tx => (
                    <tr key={tx.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{tx.date}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{tx.bank_account_name}</td>
                      <td className="px-4 py-3 text-gray-700 max-w-xs truncate">{tx.description}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${tx.transaction_type === 'Credit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {tx.transaction_type}
                        </span>
                      </td>
                      <td className={`px-4 py-3 font-semibold ${tx.transaction_type === 'Credit' ? 'text-green-700' : 'text-red-700'}`}>
                        {tx.transaction_type === 'Credit' ? '+' : '-'}US${fmt(tx.amount)}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
                          value={tx.category || ''}
                          onChange={e => updateTxStatus(tx.id, tx.status === 'Unreviewed' ? 'Categorized' : tx.status, e.target.value)}
                        >
                          <option value="">— categorize —</option>
                          {TX_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={tx.status} /></td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          {tx.status !== 'Matched' && tx.status !== 'Reconciled' && (
                            <button
                              onClick={() => updateTxStatus(tx.id, 'Matched', tx.category)}
                              className="text-xs text-blue-600 hover:underline"
                              title="Mark as Matched"
                            >Match</button>
                          )}
                          {tx.status === 'Matched' && (
                            <button
                              onClick={() => updateTxStatus(tx.id, 'Reconciled', tx.category)}
                              className="text-xs text-green-600 hover:underline"
                              title="Mark as Reconciled"
                            >Reconcile</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </section>

      {/* ── Add/Edit Bank Account Modal ── */}
      <Modal isOpen={accountModal} onClose={() => setAccountModal(false)} title={editingAccount ? 'Edit Bank Account' : 'Add Bank Account'}>
        <form onSubmit={handleSaveAccount} className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Account Name *</label>
            <input className="input" required placeholder="e.g. CBZ Operating Account"
              value={accountForm.account_name}
              onChange={e => setAccountForm(f => ({ ...f, account_name: e.target.value }))} />
          </div>
          <div>
            <label className="label">Account Type *</label>
            <select className="input" value={accountForm.account_type}
              onChange={e => setAccountForm(f => ({ ...f, account_type: e.target.value }))}>
              <option value="Operating">Operating</option>
              <option value="Credit Card">Credit Card</option>
            </select>
          </div>
          <div>
            <label className="label">Bank Name *</label>
            <input className="input" required placeholder="e.g. CBZ Bank"
              value={accountForm.bank_name}
              onChange={e => setAccountForm(f => ({ ...f, bank_name: e.target.value }))} />
          </div>
          <div>
            <label className="label">Account Number</label>
            <input className="input" placeholder="Last 4 digits or full"
              value={accountForm.account_number}
              onChange={e => setAccountForm(f => ({ ...f, account_number: e.target.value }))} />
          </div>
          <div>
            <label className="label">Opening / Current Balance (US$)</label>
            <input type="number" step="0.01" className="input" placeholder="0.00"
              value={accountForm.current_balance}
              onChange={e => setAccountForm(f => ({ ...f, current_balance: e.target.value }))} />
          </div>
          <div className="col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setAccountModal(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : editingAccount ? 'Update' : 'Add Account'}</button>
          </div>
        </form>
      </Modal>

      {/* ── Add Bank Transaction Modal ── */}
      <Modal isOpen={txModal} onClose={() => setTxModal(false)} title="Add Bank Transaction">
        <form onSubmit={handleSaveTx} className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Bank Account *</label>
            <select className="input" required value={txForm.bank_account}
              onChange={e => setTxForm(f => ({ ...f, bank_account: e.target.value }))}>
              <option value="">Select account</option>
              {bankAccounts.map(a => <option key={a.id} value={a.id}>{a.account_name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Date *</label>
            <input type="date" className="input" required value={txForm.date}
              onChange={e => setTxForm(f => ({ ...f, date: e.target.value }))} />
          </div>
          <div>
            <label className="label">Type *</label>
            <select className="input" value={txForm.transaction_type}
              onChange={e => setTxForm(f => ({ ...f, transaction_type: e.target.value }))}>
              <option value="Debit">Debit (money out)</option>
              <option value="Credit">Credit (money in)</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="label">Description *</label>
            <input className="input" required placeholder="e.g. Office rent payment"
              value={txForm.description}
              onChange={e => setTxForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div>
            <label className="label">Amount (US$) *</label>
            <input type="number" step="0.01" min="0.01" className="input" required
              value={txForm.amount}
              onChange={e => setTxForm(f => ({ ...f, amount: e.target.value }))} />
          </div>
          <div>
            <label className="label">Category</label>
            <select className="input" value={txForm.category}
              onChange={e => setTxForm(f => ({ ...f, category: e.target.value }))}>
              <option value="">Select category</option>
              {TX_CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Reference / Cheque No.</label>
            <input className="input" placeholder="Optional"
              value={txForm.reference}
              onChange={e => setTxForm(f => ({ ...f, reference: e.target.value }))} />
          </div>
          <div>
            <label className="label">Notes</label>
            <input className="input" placeholder="Optional"
              value={txForm.notes}
              onChange={e => setTxForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <div className="col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setTxModal(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Add Transaction'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
